import axios, { AxiosRequestConfig } from 'axios';
import Vue from 'vue';
import { API_TIMEOUT_DURATION } from '~/server/helpers/metrics-helper';
import CredentialStorage from '~/api/credential-storage';
import { AccessTokenStorage } from '~/api/v4/storage-type';
import { Scope } from '~/types/api/request-method';
import TokenStorage from '~/types/api/token-storage';
import { UserInfo } from '~/types/api/user-info';
import { APIResponse } from '~/types/api/api-response';

interface AccessTokenManagerConstructor {
  authEndpoint?: string;
  apiEndpoint?: string;
  appId?: string;
  credentials: CredentialStorage;
  storage: AccessTokenStorage;
  redirectUri?: string;
}

class AccessTokenManager {
  static eventBus = new Vue();
  storage: AccessTokenStorage;
  authEndpoint: string;
  appId: string;
  isFetchingToken: boolean;
  /** @deprecated */ login: boolean;
  credentials: CredentialStorage;
  private _apiEndpoint: string;
  /**
   * Creates an AccessTokenManager instance
   *
   */
  constructor(options: AccessTokenManagerConstructor) {
    this.login = false;
    this.storage = options.storage;
    this.authEndpoint = options.authEndpoint || '/_auth';
    this.appId = options.appId || '';
    this.credentials = options.credentials;
    this._apiEndpoint = options.apiEndpoint || '/api';
    this.isFetchingToken = false;
    const { token } = this.storage;
    // Ignore public token initialization in test env, hard to mock
    if (!token && process.env.NODE_ENV !== 'test') {
      this.requestPublicToken();
    }
  }

  get requestOptions(): AxiosRequestConfig {
    const userAgent = process.server ? '' : navigator.userAgent;
    return {
      headers: {
        'User-Agent-Original': userAgent,
        Identity: this.credentials.identity,
      },
    };
  }

  /**
   * Request new token
   */
  fetchFreshToken() {
    return this.authenticate();
  }

  async requestPublicToken() {
    const { data: response } = await axios.get(this.authEndpoint, this.requestOptions);
    this.storage.token = { ...response?.accounts?.credentials };
    return this.storage.token;
  }

  /**
   * Get token from storage or request new token
   */
  fetchToken(scope: Scope = 'public') {
    if (this.storage.isExpired() || !this.storage.isScopeMatch(scope)) {
      return this.fetchFreshToken();
    }

    return Promise.resolve<TokenStorage>(this.storage.token);
  }

  /** @deprecated */
  async authenticate() {
    const eventName = 'SecureClient.authorized';
    const { token } = this.storage;

    if (!this.storage.isExpired()) {
      this.login = true;
      return token;
    }

    this.destroyToken();

    const timer = setTimeout(() => {
      throw new Error('Authenticate Timeout');
    }, API_TIMEOUT_DURATION);

    const { data: response } = await axios.get(this.authEndpoint, this.requestOptions);
    const mergedResponse = {
      ...response?.accounts?.credentials,
      userId: response?.accounts?.userData?.id,
      username: response?.accounts?.userData?.username,
    };
    this.storage.token = mergedResponse;
    AccessTokenManager.eventBus.$emit(eventName, mergedResponse);
    this.login = true;
    clearTimeout(timer);
    return mergedResponse;
  }

  /**
   * Authenticate Via Bukalapak OAuth callback code
   */
  async authenticateViaCode() {
    const eventName = 'SecureClient.authorized';
    // Clear previous stalled auth when re-authenticate
    this.destroyToken();
    const timer = setTimeout(() => {
      throw new Error('OAuth Authenticate Timeout');
    }, API_TIMEOUT_DURATION);

    // Begin auth
    try {
      const { data: response } = await axios.get(this.authEndpoint, this.requestOptions);
      const mergedResponse = {
        ...response?.accounts?.credentials,
        userId: response?.accounts?.userData?.id,
        username: response?.accounts?.userData?.username,
      };
      this.storage.token = mergedResponse;
      AccessTokenManager.eventBus.$emit(eventName, mergedResponse);
      clearTimeout(timer);
      return mergedResponse;
    } catch (error) {
      clearTimeout(timer);
      throw error;
    }
  }

  async fetchUserInfo(token?: string) {
    const { data: userData } = await axios.get<APIResponse<UserInfo>>(`${this._apiEndpoint}/user-profiles`, {
      params: { access_token: token || this.storage.token.access_token },
    });
    return { userId: userData.data?.id, username: userData.data?.username };
  }

  /**
   * @deprecated
   * use storage.clear() directly instead
   **/
  destroyToken() {
    this.login = false;
    this.storage.clear();
  }
}

export default AccessTokenManager;
